import { Grid } from "@mui/material";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";

import { resolveActivePanelContentKey } from "~/helpers/panelKey";
import { useAppSelector } from "~/hooks/reduxHooks";
import SidePanel from "~/layout/SidePanel";
import { DockPanelKeyContext } from "~/providers/DockPanelContext";
import SidePanelContext from "~/providers/SidePanelContext";
import { SidePanelProviderProps, SidePanelRegistry } from "~/providers/SidePanelTypes";

const SidePanelProvider: FC<SidePanelProviderProps> = ({ children }) => {
	const [contentByPanelKey, setContentByPanelKey] = useState<Record<string, ReactNode>>({});
	const [defaultWidth, setDefaultWidth] = useState<number | undefined>(undefined);
	const [mobileViewportScroll, setMobileViewportScrollState] = useState(false);
	const activePanelKey = useAppSelector((state) => state.layout.activePanelKey);

	const setPanelContent = useCallback((panelKey: string, content: ReactNode) => {
		setContentByPanelKey((current) => ({ ...current, [panelKey]: content }));
	}, []);

	const clearPanelContent = useCallback((panelKey: string) => {
		setContentByPanelKey((current) => {
			if (!(panelKey in current)) {
				return current;
			}
			const remaining = { ...current };
			delete remaining[panelKey];
			return remaining;
		});
	}, []);

	const setDefaultPanelWidth = useCallback((width: number) => {
		setDefaultWidth(width);
	}, []);

	const setMobileViewportScroll = useCallback((value: boolean) => {
		setMobileViewportScrollState(value);
	}, []);

	const contextValue: SidePanelRegistry = useMemo(
		() => ({
			clearPanelContent,
			setDefaultPanelWidth,
			setMobileViewportScroll,
			setPanelContent
		}),
		[clearPanelContent, setDefaultPanelWidth, setMobileViewportScroll, setPanelContent]
	);

	const activeContentKey = resolveActivePanelContentKey(Object.keys(contentByPanelKey), activePanelKey);
	const registeredContent = activeContentKey === undefined ? null : (contentByPanelKey[activeContentKey] ?? null);
	const content =
		activeContentKey === undefined || registeredContent === null ? null : (
			<DockPanelKeyContext.Provider value={activeContentKey}>{registeredContent}</DockPanelKeyContext.Provider>
		);

	return (
		<SidePanelContext.Provider value={contextValue}>
			{children}
			<Grid sx={{ height: "100%" }}>
				<SidePanel content={content} defaultWidth={defaultWidth} mobileViewportScroll={mobileViewportScroll} />
			</Grid>
		</SidePanelContext.Provider>
	);
};

export default SidePanelProvider;
